import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { useQueryClient } from 'react-query';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useDebounce } from 'src/components/helpers/useDebounce';
import { PageHeaderWithActions } from 'src/components/molecules';
import { Group, GroupAttributes } from 'src/components/organisms';
import {
  GroupType,
  useCreateGroupMutation,
  useGroupsAttributesDataQuery,
  useGroupsQuery,
} from 'src/graphql';
import { createTimestampedName } from 'src/utils';
import { Loading } from '../Loading';
import {
  BodyContainer,
  ButtonWithIcon,
  EmptyStateBody,
  EmptyStateContainer,
  EmptyStateDescription,
  EmptyStateTitle,
  GroupListItem,
  GroupsBody,
  GroupsContainer,
  PageContainer,
  Summary,
  SummaryStat,
  SummaryStatRow,
  SummaryTitle,
} from './Groups.styles';

const DEBOUNCE_WAIT = 500;

export const Groups = () => {
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const queryClient = useQueryClient();
  const { data: groups, isLoading } = useGroupsQuery();
  const { data: attributesData } = useGroupsAttributesDataQuery();

  const [newGroup, setNewGroup] = useState<string | null>(null);
  const newGroupRef = useRef<HTMLLIElement>(null);

  const attributes = useMemo<GroupAttributes>(() => {
    const departments = attributesData?.groupsAttributesData?.department ?? [];
    const sites = attributesData?.groupsAttributesData?.site ?? [];
    const countries = attributesData?.groupsAttributesData?.country ?? [];
    const teams = attributesData?.groupsAttributesData?.team ?? [];
    const systemRoles = attributesData?.groupsAttributesData?.systemRoles ?? [];

    return {
      department: departments.map((department) => ({
        label: department,
        value: department,
      })),
      site: sites.map((site) => ({ label: site, value: site })),
      country: countries.map((country) => ({ label: country, value: country })),
      team: teams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
      systemRoles: systemRoles.map((systemRole) => ({
        label: systemRole.name,
        value: systemRole.id,
      })),
    };
  }, [attributesData?.groupsAttributesData]);

  useEffect(() => {
    if (newGroup) {
      const group = newGroupRef.current;
      if (group) {
        group.scrollIntoView({ behavior: 'smooth' });
        setNewGroup(null);
      }
    }
  }, [newGroup, groups]);

  const { mutate: createGroup } = useCreateGroupMutation({
    onSuccess: (data) => {
      queryClient.refetchQueries({ queryKey: ['Groups'] });
      setNewGroup(data.createGroup.id);
    },
  });

  const debounceCreateGroup = useDebounce(() => {
    createGroup({
      parameters: { name: createTimestampedName('Untitled Group Name') },
    });
  }, DEBOUNCE_WAIT);

  const handleOnClickCreateGroup = () => debounceCreateGroup();

  const noGroups = groups?.orgGroups.length === 0;

  if (isLoading) {
    return <Loading />;
  }

  if (noGroups) {
    return (
      <PageContainer data-testid="groups-page">
        <BodyContainer>
          <PageHeaderWithActions
            title="Groups"
            createItem={handleOnClickCreateGroup}
            createButtonText="Create new group"
          />
          <EmptyStateContainer data-testid="empty-group-container">
            <img src={emptyStateSrc} alt="Empty state" width={'380px'} />
            <EmptyStateTitle>Welcome to Groups</EmptyStateTitle>
            <EmptyStateBody>
              <EmptyStateDescription>
                Create groups based on user properties. Reports can then be
                routed to these groups based on the criteria that they match.
                Click &apos;Create new group&apos; to get started.
              </EmptyStateDescription>
              <ButtonWithIcon
                size="medium"
                variant="primary"
                onClick={() => handleOnClickCreateGroup()}
                data-testid="create-new-group-button-empty"
              >
                <StyledIcon size="24px" className="uil uil-plus" />
                Create new group
              </ButtonWithIcon>
            </EmptyStateBody>
          </EmptyStateContainer>
        </BodyContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid="groups-page">
      <BodyContainer>
        <PageHeaderWithActions
          title="Groups"
          createItem={handleOnClickCreateGroup}
          createButtonText="Create new group"
        />
        <GroupsBody data-testid="groups-body">
          <GroupsContainer rows={groups?.orgGroups.length ?? 1}>
            {groups?.orgGroups?.map((group, index) => (
              <GroupListItem
                data-testid="group-list-item"
                row={index}
                key={group.id}
                ref={group.id === newGroup ? newGroupRef : null}
              >
                <Group
                  group={group as GroupType}
                  index={index}
                  groupsLength={groups.orgGroups.length}
                  attributes={attributes}
                />
              </GroupListItem>
            ))}
            <Summary>
              <SummaryTitle>Summary</SummaryTitle>
              <SummaryStatRow>
                <SummaryStat>Total groups created</SummaryStat>
                <SummaryStat>{groups?.orgGroups.length}</SummaryStat>
              </SummaryStatRow>
            </Summary>
          </GroupsContainer>
        </GroupsBody>
      </BodyContainer>
    </PageContainer>
  );
};
